/**
 * Credits Mock 数据
 */

import {
    TenantType,
    PlanType,
    TierLevel,
    SubscriptionStatus,
    UsageRecordStatus,
    SettlementMode,
    CreditChangeType,
    GrantType,
    AllocationType,
    OrgCreditStatus,
    type CreditsSummary,
    type UsageRecord,
    type CreditChangeRecord,
    type UpgradeConfirmInfo,
    type DowngradeConfirmInfo,
    type RechargeRecordsResponse,
    type ConsumeRecordsResponse,
    type PersonalCreditsDetailResponse,
    type CompanyCreditsDetailResponse,
} from '../types';

// ==================== Free 用户 Mock 数据 ====================

export const MOCK_FREE_USER_SUMMARY: CreditsSummary = {
    totalAvailable: 8000,
    freeCredits: {
        available: 8000,
        nearestExpiration: {
            amount: 3000,
            expiresAt: '2026-06-18T00:00:00Z',
        },
        nextClaimDate: '2026-06-23T00:00:00Z',
    },
    paidCredits: {
        available: 0,
        grants: [],
    },
    tenantType: TenantType.PERSONAL,
    planType: PlanType.FREE,
    canUpgrade: true,
    canManageBilling: false,
    hasLegacyEntitlement: false,
};

/** Free 用户 - 多笔即将过期场景 */
export const MOCK_FREE_USER_MULTIPLE_EXPIRING: CreditsSummary = {
    totalAvailable: 8000,
    freeCredits: {
        available: 5000,
        nearestExpiration: {
            amount: 1000,
            expiresAt: '2026-06-01T00:00:00Z',
        },
        nextClaimDate: '2026-06-23T00:00:00Z',
    },
    paidCredits: {
        available: 3000,
        grants: [
            { grantId: 'grant_bonus_1', remaining: 3000, expiresAt: '2026-06-02T00:00:00Z' },
            { grantId: 'grant_bonus_2', remaining: 1000, expiresAt: '2026-06-04T00:00:00Z' },
            { grantId: 'grant_bonus_3', remaining: 3000, expiresAt: '2026-06-06T00:00:00Z' },
            { grantId: 'grant_bonus_4', remaining: 3000, expiresAt: '2026-06-08T00:00:00Z' }
        ],
    },
    tenantType: TenantType.PERSONAL,
    planType: PlanType.FREE,
    canUpgrade: true,
    canManageBilling: false,
    hasLegacyEntitlement: false,
};

// ==================== Pro 用户 Mock 数据 ====================

export const MOCK_PRO_USER_SUMMARY: CreditsSummary = {
    totalAvailable: 68000,
    freeCredits: {
        available: 18000,
        nearestExpiration: {
            amount: 2000,
            expiresAt: '2026-06-06T00:00:00Z',
        },
    },
    paidCredits: {
        available: 50000,
        grants: [
            { grantId: 'grant_paid_001', remaining: 8000, expiresAt: '2026-06-04T00:00:00Z' },
            { grantId: 'grant_paid_002', remaining: 5000, expiresAt: '2026-06-10T00:00:00Z' },
            { grantId: 'grant_paid_003', remaining: 3000, expiresAt: '2026-06-12T00:00:00Z' },
            { grantId: 'grant_paid_004', remaining: 34000, expiresAt: '2026-07-01T00:00:00Z' }
        ],
    },
    subscription: {
        currentTier: TierLevel.PRO_200,
        currentPlan: PlanType.PRO,
        status: SubscriptionStatus.ACTIVE,
        currentPeriodStart: '2026-05-01T00:00:00Z',
        currentPeriodEnd: '2026-06-01T00:00:00Z',
        renewalDate: '2024-10-26T00:00:00Z',
    },
    tenantType: TenantType.PERSONAL,
    planType: PlanType.PRO,
    canUpgrade: true,
    canManageBilling: true,
    hasLegacyEntitlement: false,
};

// ==================== Pro 用户（有降级预约）Mock 数据 ====================

export const MOCK_PRO_USER_WITH_DOWNGRADE_SUMMARY: CreditsSummary = {
    totalAvailable: 45000,
    freeCredits: {
        available: 5000,
        nearestExpiration: {
            amount: 5000,
            expiresAt: '2026-06-15T00:00:00Z',
        },
    },
    paidCredits: {
        available: 40000,
        grants: [
            {
                grantId: 'grant_002',
                remaining: 40000,
                expiresAt: '2026-07-01T00:00:00Z',
            }
        ],
    },
    subscription: {
        currentTier: TierLevel.PRO_200,
        currentPlan: PlanType.PRO,
        status: SubscriptionStatus.ACTIVE,
        currentPeriodStart: '2026-05-01T00:00:00Z',
        currentPeriodEnd: '2026-06-01T00:00:00Z',
        renewalDate: '2026-06-01T00:00:00Z',
        scheduledDowngrade: {
            tier: TierLevel.PRO_100,
            effectiveAt: '2026-06-01T00:00:00Z',
        },
    },
    tenantType: TenantType.PERSONAL,
    planType: PlanType.PRO,
    canUpgrade: true,
    canManageBilling: true,
    hasLegacyEntitlement: true,
};

// ==================== Enterprise 用户 Mock 数据 ====================

/** 场景1：企业共享池（Credits 可用） */
export const MOCK_ENTERPRISE_USER_SUMMARY: CreditsSummary = {
    totalAvailable: 10000,
    freeCredits: {
        available: 0,
    },
    paidCredits: {
        available: 0,
        grants: [],
    },
    tenantType: TenantType.ENTERPRISE,
    planType: PlanType.ENTERPRISE,
    canUpgrade: false,
    canManageBilling: false,
    hasLegacyEntitlement: false,
    enterpriseStatus: {
        orgName: 'PatSnap Global',
        allocationType: AllocationType.SHARED_POOL,
        todayUsage: 200,
        orgStatus: OrgCreditStatus.ACTIVE,
    },
};

/** 场景2：企业 Credits 不可用 */
export const MOCK_ENTERPRISE_UNAVAILABLE_SUMMARY: CreditsSummary = {
    totalAvailable: 0,
    freeCredits: {
        available: 0,
    },
    paidCredits: {
        available: 0,
        grants: [],
    },
    tenantType: TenantType.ENTERPRISE,
    planType: PlanType.ENTERPRISE,
    canUpgrade: false,
    canManageBilling: false,
    hasLegacyEntitlement: false,
    enterpriseStatus: {
        orgName: 'PatSnap Global',
        allocationType: AllocationType.SHARED_POOL,
        todayUsage: 0,
        orgStatus: OrgCreditStatus.EXPIRED,
    },
};

// ==================== Enterprise Usage Records Mock 数据 ====================

export const MOCK_ENTERPRISE_USAGE_RECORDS: UsageRecord[] = [
    {
        transactionId: 'ent_txn_001',
        sessionId: 'ent_session_001',
        featureId: 'ai_search',
        displayName: 'Quarterly Revenue Analysis of Two Tech Companies in 2024',
        creditChange: -13,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T10:30:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'ai_search',
    },
    {
        transactionId: 'ent_txn_002',
        sessionId: 'ent_session_002',
        featureId: 'ai_search',
        displayName: 'Creating mood boards together',
        creditChange: -200,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T09:15:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'ai_search',
    },
    {
        transactionId: 'ent_txn_003',
        sessionId: 'ent_session_003',
        featureId: 'company_scout',
        displayName: 'Competitor Landscape Analysis with SWOT and Actionable Insights',
        creditChange: -120,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T08:45:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'company_scout',
    },
    {
        transactionId: 'ent_txn_004',
        sessionId: 'ent_session_004',
        featureId: 'patent_landscape',
        displayName: 'Patent landscape summary',
        creditChange: -80,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T08:20:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'patent_landscape',
    },
    {
        transactionId: 'ent_txn_005',
        sessionId: 'ent_session_005',
        featureId: 'translation',
        displayName: 'Generate the translation documen',
        creditChange: -2,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T07:00:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'translation',
    },
    {
        transactionId: 'ent_txn_006',
        sessionId: 'ent_session_006',
        featureId: 'fto_search',
        displayName: 'FTO Search: This product is a 28W simple-style combined ceiling fan and light f...',
        creditChange: 0,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T06:30:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'fto_search',
    },
    {
        transactionId: 'ent_txn_007',
        sessionId: 'ent_session_007',
        featureId: 'compliance',
        displayName: 'EU AI Act compliance and hallucination disclosures',
        creditChange: -12,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-04-10T05:45:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'compliance',
    }
];

// ==================== Usage Records Mock 数据 ====================

export const MOCK_USAGE_RECORDS: UsageRecord[] = [
    {
        transactionId: 'txn_001',
        sessionId: 'session_001',
        jobId: 'job_001',
        featureId: 'ai_search',
        displayName: 'AI Search - Patent Landscape Analysis',
        creditChange: -150,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-05-19T10:30:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_abc123',
        agentType: 'ai_search',
    },
    {
        transactionId: 'txn_002',
        sessionId: 'session_002',
        jobId: 'job_002',
        featureId: 'tech_research',
        displayName: 'Tech Research - Solid State Battery',
        creditChange: -280,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-05-19T09:15:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_def456',
        agentType: 'tech_research',
    },
    {
        transactionId: 'txn_003',
        sessionId: 'session_003',
        jobId: 'job_003',
        featureId: 'company_scout',
        displayName: 'Company Scout - EV Battery Manufacturers',
        creditChange: -420,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-05-18T16:45:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_ghi789',
        agentType: 'company_scout',
    },
    {
        transactionId: 'txn_004',
        sessionId: 'session_004',
        jobId: 'job_004',
        featureId: 'patent_draft',
        displayName: 'Patent Disclosure Draft',
        creditChange: -350,
        status: UsageRecordStatus.PARTIALLY_COMPLETED,
        settlementTime: '2026-05-18T14:20:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_jkl012',
        agentType: 'patent_draft',
    },
    {
        transactionId: 'txn_005',
        sessionId: 'session_005',
        jobId: 'job_005',
        featureId: 'fto_search',
        displayName: 'FTO Search - Lithium Ion Technology',
        creditChange: -500,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-05-17T11:00:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_mno345',
        agentType: 'fto_search',
    },
    {
        transactionId: 'txn_006',
        sessionId: 'session_006',
        featureId: 'ai_search',
        displayName: 'AI Search - Competitor Analysis',
        creditChange: -180,
        status: UsageRecordStatus.FAILED,
        settlementTime: '2026-05-17T09:30:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        agentType: 'ai_search',
    },
    {
        transactionId: 'txn_007',
        sessionId: 'session_007',
        jobId: 'job_007',
        featureId: 'novelty_search',
        displayName: 'Novelty Search - Battery Management System',
        creditChange: -320,
        status: UsageRecordStatus.COMPLETED,
        settlementTime: '2026-05-16T15:45:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_pqr678',
        agentType: 'novelty_search',
    },
    {
        transactionId: 'txn_008',
        sessionId: 'session_008',
        jobId: 'job_008',
        featureId: 'tech_research',
        displayName: 'Tech Research - Hydrogen Fuel Cell',
        creditChange: -250,
        status: UsageRecordStatus.REFUNDED,
        settlementTime: '2026-05-16T10:20:00Z',
        settlementMode: SettlementMode.CREDIT_CHARGE,
        historySessionId: 'conv_stu901',
        agentType: 'tech_research',
    }
];

// ==================== Credit Changes Mock 数据 ====================

export const MOCK_CREDIT_CHANGES: CreditChangeRecord[] = [
    {
        changeId: 'change_001',
        changeType: CreditChangeType.PAID_GRANT,
        grantId: 'grant_001',
        grantType: GrantType.PAID,
        creditChange: 120000,
        balanceAfter: 120000,
        effectiveAt: '2026-05-01T00:00:00Z',
        expiresAt: '2026-06-01T00:00:00Z',
        displayName: 'Pro 200 Monthly Credits',
    },
    {
        changeId: 'change_002',
        changeType: CreditChangeType.FREE_GRANT,
        grantId: 'grant_002',
        grantType: GrantType.FREE,
        creditChange: 5000,
        balanceAfter: 125000,
        effectiveAt: '2026-05-01T00:00:00Z',
        expiresAt: '2026-06-01T00:00:00Z',
        displayName: 'Monthly Free Credits',
    },
    {
        changeId: 'change_003',
        changeType: CreditChangeType.RENEWAL_GRANT,
        grantId: 'grant_003',
        grantType: GrantType.PAID,
        creditChange: 120000,
        balanceAfter: 135000,
        effectiveAt: '2026-04-01T00:00:00Z',
        expiresAt: '2026-05-01T00:00:00Z',
        displayName: 'Pro 200 Renewal Credits',
    },
    {
        changeId: 'change_004',
        changeType: CreditChangeType.EXPIRATION,
        grantId: 'grant_old',
        creditChange: -15000,
        balanceAfter: 120000,
        effectiveAt: '2026-04-01T00:00:00Z',
        displayName: 'Expired Credits',
    },
    {
        changeId: 'change_005',
        changeType: CreditChangeType.FREE_CLAIM,
        grantId: 'grant_004',
        grantType: GrantType.FREE,
        creditChange: 5000,
        balanceAfter: 140000,
        effectiveAt: '2026-03-15T00:00:00Z',
        expiresAt: '2026-04-15T00:00:00Z',
        displayName: 'Claimed Free Credits',
    },
    {
        changeId: 'change_006',
        changeType: CreditChangeType.STANDALONE_REFUND,
        creditChange: 250,
        balanceAfter: 140250,
        effectiveAt: '2026-03-10T00:00:00Z',
        expiresAt: '2026-04-10T00:00:00Z',
        displayName: 'Service Refund',
    },
    {
        changeId: 'change_007',
        changeType: CreditChangeType.TRIAL_GRANT,
        grantId: 'grant_trial',
        grantType: GrantType.FREE,
        creditChange: 10000,
        balanceAfter: 10000,
        effectiveAt: '2026-02-01T00:00:00Z',
        expiresAt: '2026-03-01T00:00:00Z',
        displayName: 'Trial Credits',
    }
];

// ==================== Upgrade/Downgrade Confirm Info ====================

export const MOCK_UPGRADE_CONFIRM_INFO: UpgradeConfirmInfo = {
    currentPlan: PlanType.PRO,
    currentTier: TierLevel.PRO_200,
    targetTier: TierLevel.PRO_400,
    paymentAmount: 200,
    currency: 'USD',
    newPeriodStart: '2026-05-19T00:00:00Z',
    newPeriodEnd: '2026-06-19T00:00:00Z',
    creditsToReceive: 280000,
    existingCreditsRetained: true,
    nextRenewalAmount: 400,
    nextRenewalDate: '2026-06-19T00:00:00Z',
};

export const MOCK_DOWNGRADE_CONFIRM_INFO: DowngradeConfirmInfo = {
    currentPlan: PlanType.PRO,
    currentTier: TierLevel.PRO_200,
    targetTier: TierLevel.PRO_100,
    effectiveDate: '2026-06-01T00:00:00Z',
    currentPeriodEnd: '2026-06-01T00:00:00Z',
    currentCreditsRetained: true,
    nextPeriodCredits: 50000,
    nextRenewalAmount: 100,
};

// ==================== 当前使用的 Mock 数据配置 ====================

// 切换不同用户类型进行测试：
// 'free' | 'free_multi_expiring' | 'pro' | 'pro_downgrade' | 'enterprise' | 'enterprise_unavailable'
export const MOCK_USER_TYPE: 'free' | 'free_multi_expiring' | 'pro' | 'pro_downgrade' | 'enterprise' | 'enterprise_unavailable' = 'pro';

export function getMockSummary(): CreditsSummary {
    switch (MOCK_USER_TYPE) {
    case 'free':
        return MOCK_FREE_USER_SUMMARY;
    case 'free_multi_expiring':
        return MOCK_FREE_USER_MULTIPLE_EXPIRING;
    case 'pro_downgrade':
        return MOCK_PRO_USER_WITH_DOWNGRADE_SUMMARY;
    case 'enterprise':
        return MOCK_ENTERPRISE_USER_SUMMARY;
    case 'enterprise_unavailable':
        return MOCK_ENTERPRISE_UNAVAILABLE_SUMMARY;
    case 'pro':
    default:
        return MOCK_PRO_USER_SUMMARY;
    }
}

export function getMockUsageRecords(): UsageRecord[] {
    if (MOCK_USER_TYPE === 'enterprise') {
        return MOCK_ENTERPRISE_USAGE_RECORDS;
    }
    return MOCK_USAGE_RECORDS;
}

// ==================== 充值记录 Mock 数据 ====================

export const MOCK_RECHARGE_RECORDS: RechargeRecordsResponse = {
    totalCredits: 212234,
    usedCredits: 500,
    items: [
        {
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            credits: 50000,
            source: 'Pro Subscription',
            sourceId: 'sub_001',
            beginAt: 1714521600000, // 2024-05-01
            endAt: 1717199999000, // 2024-05-31
            remark: 'Monthly subscription credits',
            rechargeAt: 1714521600000,
        },
        {
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            credits: 5000,
            source: 'Monthly Refresh',
            sourceId: 'free_001',
            beginAt: 1714521600000,
            endAt: 1717199999000,
            remark: 'Free monthly credits',
            rechargeAt: 1714521600000,
        },
        {
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            credits: 10000,
            source: 'Bonus',
            sourceId: 'bonus_001',
            beginAt: 1711929600000, // 2024-04-01
            endAt: 1714521599000, // 2024-04-30
            remark: 'Promotional bonus',
            rechargeAt: 1711929600000,
        },
        {
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            credits: 50000,
            source: 'Pro Subscription',
            sourceId: 'sub_002',
            beginAt: 1711929600000,
            endAt: 1714521599000,
            remark: 'Monthly subscription credits',
            rechargeAt: 1711929600000,
        }
    ],
};

// ==================== 消费记录 Mock 数据 ====================

export const MOCK_CONSUME_RECORDS: ConsumeRecordsResponse = {
    items: [
        {
            id: 'a92a0c7668954eb988addff92f2281a0',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'ai_search',
            featureNameCn: '季度收入分析',
            featureNameEn: 'Quarterly Revenue Analysis of Two Tech Companies in 2024',
            featureNameJp: '四半期収益分析',
            featureNameTw: '季度收入分析',
            jobId: 'job001',
            deductCredits: 13,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 13,
            consumeAt: 1712736000000, // 2024-04-10
            createdAt: 1712736000000,
            balanceBeforeDeduct: 50000,
        },
        {
            id: 'b82b1c8779065fc099beeg03g3392b1',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'ai_search',
            featureNameCn: '创建情绪板',
            featureNameEn: 'Creating mood boards together',
            featureNameJp: 'ムードボード作成',
            featureNameTw: '創建情緒板',
            jobId: 'job002',
            deductCredits: 200,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 200,
            consumeAt: 1712732400000,
            createdAt: 1712732400000,
            balanceBeforeDeduct: 49987,
        },
        {
            id: 'c73c2d9880176gd100cfgh14h4403c2',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'company_scout',
            featureNameCn: '竞争格局分析',
            featureNameEn: 'Competitor Landscape Analysis with SWOT and Actionable Insights',
            featureNameJp: '競合分析',
            featureNameTw: '競爭格局分析',
            jobId: 'job003',
            deductCredits: 120,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 120,
            consumeAt: 1712728800000,
            createdAt: 1712728800000,
            balanceBeforeDeduct: 49787,
        },
        {
            id: 'd64d3ea991287he211dghi25i5514d3',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'free_grant',
            featureNameCn: '月度刷新',
            featureNameEn: 'Monthly Refresh',
            featureNameJp: '月次リフレッシュ',
            featureNameTw: '月度刷新',
            jobId: '',
            deductCredits: -300,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 0,
            consumeAt: 1712725200000,
            createdAt: 1712725200000,
            balanceBeforeDeduct: 49667,
        },
        {
            id: 'e55e4fb002398if322ehij36j6625e4',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'translation',
            featureNameCn: '生成翻译文档',
            featureNameEn: 'Generate the translation documen',
            featureNameJp: '翻訳ドキュメント生成',
            featureNameTw: '生成翻譯文檔',
            jobId: 'job005',
            deductCredits: 2,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 2,
            consumeAt: 1712721600000,
            createdAt: 1712721600000,
            balanceBeforeDeduct: 49967,
        },
        {
            id: 'f46f5gc113409jg433fijk47k7736f5',
            tenantUserId: '6a207a638a4a4f62888b141492f7808d',
            companyId: '',
            platform: 'web',
            productId: 'pro',
            deductType: 'feature',
            featureId: 'fto_search',
            featureNameCn: 'FTO搜索',
            featureNameEn: 'FTO Search: This product is a 28W simple-style combined ceiling fan and light f...',
            featureNameJp: 'FTO検索',
            featureNameTw: 'FTO搜索',
            jobId: 'job006',
            deductCredits: 0,
            quantity: 1,
            unit: '',
            status: 'success',
            creditsType: 'personal',
            unitPrice: 0,
            consumeAt: 1712718000000,
            createdAt: 1712718000000,
            balanceBeforeDeduct: 49965,
        }
    ],
    totalSize: 6,
    pageSize: 20,
};

// ==================== 个人积分详情 Mock 数据 ====================

export const MOCK_PERSONAL_CREDITS_DETAIL: PersonalCreditsDetailResponse = {
    total_credits: 1000,
    used_credits: 120,
    daily_used_credits: 20,
    items: [
        {
            tenant_user_id: 'tenant_user_001',
            credits: 500,
            source: 'ACTIVITY',
            source_id: 'activity_001',
            charge_mode: 'TRIAL',
            begin_at: 1714521600000,
            end_at: 1746057600000,
            remark: 'Activity gift credits',
            recharge_at: 1714521600000,
            used_credits: 80,
        },
        {
            tenant_user_id: 'tenant_user_001',
            credits: 500,
            source: 'SUBSCRIPTION',
            source_id: 'sub_001',
            charge_mode: 'COMMERCIAL',
            begin_at: 1711929600000,
            end_at: 1714521599000,
            remark: 'Monthly subscription credits',
            recharge_at: 1711929600000,
            used_credits: 40,
        }
    ],
};

// ==================== 公司积分详情 Mock 数据 ====================

export const MOCK_COMPANY_CREDITS_DETAIL: CompanyCreditsDetailResponse = {
    total_credits: 50000,
    used_credits: 3200,
    daily_used_credits: 80,
    user_daily_limit: 100,
    company_user_daily_limit: 500,
    company_total_daily_limit: 5000,
    items: [
        {
            company_id: 'company_001',
            credits: 30000,
            source: 'CONTRACT',
            charge_mode: 'COMMERCIAL',
            begin_at: 1714521600000,
            end_at: 1746057600000,
            contract_no: 'CN-2024-001',
            remark: 'Annual contract recharge',
            recharge_at: 1714521600000,
            created_by: 'admin',
            created_at: 1714521600000,
        },
        {
            company_id: 'company_001',
            credits: 20000,
            source: 'CONTRACT',
            charge_mode: 'COMMERCIAL',
            begin_at: 1711929600000,
            end_at: 1714521599000,
            contract_no: 'CN-2024-002',
            remark: 'Q1 contract recharge',
            recharge_at: 1711929600000,
            created_by: 'admin',
            created_at: 1711929600000,
        }
    ],
};
